import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { texts } from 'utils/languages/fr';
import { CustomTextField, ActionButton, CheckboxLabel, ErrorMessage } from 'components';
import {
    Box,
    Card,
    CardHeader,
    Checkbox,
    Divider,
    Grid,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import * as icons from 'mdi-material-ui';
import { useSmallScreen } from 'utils/hooks';

function not(a, b) {
    return a.filter(value => !b.find(x => x.id === value.id));
}

function intersection(a, b) {
    return a.filter(value => !!b.find(x => x.id === value.id));
}

function TransferListItem({ id, label, checked, disabled, onClick }) {
    return (
        <ListItemButton
            disableGutters
            disableRipple
            disabled={disabled}
            onClick={onClick}
            title={label}
            sx={{ width: '100%' }}
        >
            <ListItemIcon>
                <Checkbox
                    checked={checked}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': id }}
                />
            </ListItemIcon>
            <ListItemText
                id={id}
                primaryTypographyProps={{ noWrap: true, maxWidth: '90%' }}
                primary={label}
            />
        </ListItemButton>
    );
}

TransferListItem.propTypes = {
    id: PropTypes.string,
    label: PropTypes.string,
    checked: PropTypes.bool,
    disabled: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
};

function TransferListSide({
    permanentItems,
    items,
    side,
    checkedItems,
    loading,
    renderText,
    textKey,
    checkAll,
    onToggle,
    onSelectAll,
}) {
    const [keyword, setKeyword] = useState('');
    const handleSearch = useCallback(e => setKeyword(e.target.value), []);
    const handleClear = useCallback(e => setKeyword(''), []);

    const allItemsArePermanent = useMemo(() => {
        if (!permanentItems || permanentItems.length === 0) return false;
        return (
            items.length === permanentItems.length &&
            items.every(item => permanentItems.find(p => p.id === item.id))
        );
    }, [items, permanentItems]);

    const checkeditemsBelongToSide = useMemo(() => {
        return checkedItems.every(item => items.find(i => i.id === item.id));
    }, [checkedItems, items]);

    return (
        <Grid item xs={12} lg={5} pl={0}>
            <Card elevation={1}>
                <CardHeader
                    title={
                        <Box display="flex" flexDirection="column" justifyContent="center" gap={2}>
                            <CustomTextField
                                formControlProps={{ sx: { mb: 0 } }}
                                value={keyword}
                                onChange={handleSearch}
                                placeholder={texts.SEARCH}
                                endAdornment={
                                    <InputAdornment position="start" onClick={handleClear}>
                                        <icons.Close
                                            sx={{
                                                ursor: 'pointer',
                                                fontSize: '20px',
                                                color: 'grey.400',
                                            }}
                                        />
                                    </InputAdornment>
                                }
                            />
                            <CheckboxLabel
                                id="select-all"
                                controlSx={{ pl: '4px' }}
                                label={texts.SELECT_ALL}
                                checked={checkAll === side}
                                disabled={side === 'right' && allItemsArePermanent}
                                sx={{ mb: 0 }}
                                indeterminate={
                                    checkedItems?.length > 0 &&
                                    checkedItems.length < items.length &&
                                    checkeditemsBelongToSide
                                }
                                onChange={onSelectAll(items, side)}
                            />
                        </Box>
                    }
                />
                <Divider />
                <List
                    sx={{ maxHeight: '250px', height: '250px', overflow: 'auto', width: '100%' }}
                    dense
                    component="div"
                    disablePadding
                >
                    {items
                        .filter(
                            v =>
                                !keyword ||
                                (renderText?.(v) || v[textKey])
                                    .toLowerCase()
                                    .includes(keyword.toLowerCase())
                        )
                        .map(val => (
                            <TransferListItem
                                key={`transfer-list-all-item-${val.id}-label`}
                                id={`transfer-list-all-item-${val.id}-label`}
                                label={renderText?.(val) || val[textKey]}
                                disabled={
                                    loading ||
                                    (side === 'right' &&
                                        !!permanentItems?.find(item => item.id === val.id)) // can't remove a permanent item
                                }
                                onClick={onToggle(val)}
                                checked={checkedItems.indexOf(val) !== -1}
                            />
                        ))}
                </List>
            </Card>
        </Grid>
    );
}

TransferListSide.propTypes = {
    permanentItems: PropTypes.array,
    items: PropTypes.array,
    side: PropTypes.oneOf(['right', 'left']),
    checkedItems: PropTypes.array,
    loading: PropTypes.bool,
    renderText: PropTypes.func,
    textKey: PropTypes.string,
    checkAll: PropTypes.string,
    onToggle: PropTypes.func,
    onSelectAll: PropTypes.func,
};

export default function TransferList(props) {
    let {
        permanentItems,
        items,
        value,
        onChange,
        textKey,
        renderText,
        label,
        error,
        sorterFunction,
        loading,
        ...rest
    } = props;
    const isSmall = useSmallScreen('lg');
    const [checkedItems, setCheckedItems] = useState([]);
    const [checkAll, setCheckAll] = useState('');
    const left = useMemo(
        () => [...items, ...value].filter(x => !value.find(v => v.id === x.id)),
        [items, value]
    );

    const leftChecked = intersection(checkedItems, left);
    const rightChecked = intersection(checkedItems, value);

    const handleToggle = useCallback(
        value => () => {
            const currentIndex = checkedItems.findIndex(x => x.id === value.id);
            const newCheckedItems = [...checkedItems];

            if (currentIndex === -1) {
                newCheckedItems.push(value);
            } else {
                newCheckedItems.splice(currentIndex, 1);
            }

            setCheckedItems(newCheckedItems);
        },
        [checkedItems]
    );

    const handleSelectAll = useCallback(
        (value, side) => e => {
            const { checked } = e.target;
            setCheckedItems(() => {
                if (checked) {
                    setCheckAll(side);
                    return side === 'left'
                        ? value
                        : value.filter(item => !permanentItems?.find(p => p.id === item.id));
                } else {
                    setCheckAll('');
                    return [];
                }
            });
        },
        [permanentItems]
    );

    const handleCheckedRight = () => {
        onChange(value.concat(leftChecked));
        setCheckedItems(not(checkedItems, leftChecked));
        setCheckAll('');
    };

    const handleCheckedLeft = () => {
        onChange(not(value, rightChecked));
        setCheckedItems(not(checkedItems, rightChecked));
        setCheckAll('');
    };

    return (
        <Box display="flex" flexDirection="column" marginY={2} {...rest}>
            <Grid container spacing={0} justifyContent="center" alignItems="center" width="100%">
                <Grid item xs={12}>
                    <Typography gutterBottom>{label}</Typography>
                </Grid>
                <TransferListSide
                    items={sorterFunction ? [...left].sort(sorterFunction) : left}
                    checkedItems={checkedItems}
                    loading={loading}
                    textKey={textKey}
                    renderText={renderText}
                    checkAll={checkAll}
                    onToggle={handleToggle}
                    onSelectAll={handleSelectAll}
                    side="left"
                />
                <Grid item xs={1} lg={2} pl={0}>
                    <Box
                        display="flex"
                        sx={theme => ({
                            flexDirection: 'column',
                            justifyContent: 'center',
                            [theme.breakpoints.down('lg')]: {
                                flexDirection: 'row',
                                alignItems: 'center',
                            },
                        })}
                        alignItems="center"
                        gap="10px"
                    >
                        <ActionButton
                            variant="outlined"
                            size="small"
                            sx={{ padding: '6px 12px' }}
                            onClick={handleCheckedRight}
                            disabled={leftChecked.length === 0}
                        >
                            {isSmall ? <icons.ChevronDown /> : <icons.ChevronRight />}
                        </ActionButton>
                        <ActionButton
                            variant="outlined"
                            size="small"
                            sx={{ padding: '6px 12px' }}
                            onClick={handleCheckedLeft}
                            disabled={rightChecked.length === 0}
                        >
                            {isSmall ? <icons.ChevronUp /> : <icons.ChevronLeft />}
                        </ActionButton>
                    </Box>
                </Grid>
                <TransferListSide
                    items={sorterFunction ? [...value].sort(sorterFunction) : value}
                    permanentItems={permanentItems}
                    checkedItems={checkedItems}
                    loading={loading}
                    textKey={textKey}
                    renderText={renderText}
                    checkAll={checkAll}
                    onToggle={handleToggle}
                    onSelectAll={handleSelectAll}
                    side="right"
                />
            </Grid>
            <ErrorMessage align="center" message={error} />
        </Box>
    );
}

TransferList.propTypes = {
    permanentItems: PropTypes.any,
    items: PropTypes.any.isRequired,
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.node.isRequired,
    error: PropTypes.string,
    textKey: PropTypes.string,
    renderText: PropTypes.func,
    sorterFunction: PropTypes.func,
    loading: PropTypes.bool,
};
